import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, catchError, map, mergeMap, of, switchMap, take, tap } from 'rxjs';

import { RestapiService } from '@app/services/restapi.service';
import { TurnoverConfigurationStepAssigneesService } from '@main-application/management/pages/turnover-configuration/components/turnover-configuration-step-assignees/services/turnover-configuration-step-assignees.service';
import { createResponsibleParties } from '@main-application/management/pages/turnover-configuration/components/turnover-configuration-step-assignees/store/actions/turnover-configuration-step-assignees.actions';
import { TurnoverTransitionSource } from '@shared/enums/turnover-transition-source';
import { UserType } from '@shared/enums/user-type';
import { WorkflowStepEnumType } from '@shared/enums/workflow-step.enum';
import { RestWorkflowStepAssignmentModel } from '@shared/interfaces/rest-workflow-step-assignment-model.interface';
import {
  RestTurnoverDataModel,
  RestTurnoverTaskModel,
  isRestTurnoverTaskModel,
} from '@shared/interfaces/turnover.interface';
import { WorkflowAction } from '@shared/interfaces/workflow-action.interface';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { ModalsService } from '@ui-components/modals/modals.service';

@Injectable({
  providedIn: 'root',
})
export class WorkflowActionsService {
  private restApiService: RestapiService = inject(RestapiService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private responsiblePartiesService: TurnoverConfigurationStepAssigneesService = inject(
    TurnoverConfigurationStepAssigneesService
  );
  protected modalsService: ModalsService = inject(ModalsService);
  protected store: Store<{}> = inject(Store);

  isModalOpen = false;

  transitWorkflowStepAssignee(
    turnoverId: number,
    stepType: WorkflowStepEnumType,
    assigneeId: number,
    source: TurnoverTransitionSource
  ): Observable<boolean> {
    return this.restApiService
      .create<boolean>(`turnovers/${turnoverId}/transit/${stepType}/${assigneeId}?source=${source}`, {})
      .pipe(
        catchError(err => {
          const errorMsg =
            this.snackbarService.getErrorCodeMsg(err) ?? SnackbarErrorMessage.ErrorUpdatingTurnoverStepAssignee;
          this.snackbarService.error(errorMsg);
          throw err;
        })
      );
  }

  setWorkflowStepAssignee(
    turnoverId: number,
    stepType: WorkflowStepEnumType,
    assigneeId: number,
    rememberIt = false
  ): Observable<boolean> {
    return this.restApiService
      .associate<boolean>(`WorkflowActions/${turnoverId}/${stepType}/${assigneeId}?rememberIt=${rememberIt}`, null)
      .pipe(
        catchError(err => {
          const errorMsg =
            this.snackbarService.getErrorCodeMsg(err) ?? SnackbarErrorMessage.ErrorUpdatingTurnoverStepAssignee;
          this.snackbarService.error(errorMsg);
          throw err;
        })
      );
  }

  moveToNextStep(
    turnoverId: number,
    stepType: WorkflowStepEnumType,
    assigneeId: number | null,
    boardId: number = null,
    rememberIt = false
  ) {
    if (typeof assigneeId === 'number')
      return this.restApiService.post(
        `turnovers/${turnoverId}/transit/${stepType}/${assigneeId}?rememberIt=${rememberIt}&source=${
          boardId ? `1&sourceId=${boardId}` : '0'
        }`
      );
    return this.restApiService.post(
      `turnovers/${turnoverId}/transit/${stepType}?source=${boardId ? `1&sourceId=${boardId}` : '0'}`
    );
  }

  moveToNextStepWithDefaultAssignee(
    turnover: (RestTurnoverDataModel | RestTurnoverTaskModel) & { projectTypeId?: number },
    stepType: WorkflowStepEnumType,
    boardId: number = null
  ) {
    if (turnover?.projectTypeId) {
      return this.moveToNextStep(turnover.id, stepType, null, boardId, false).pipe(map(() => true));
    }
    return this.getAssignee(turnover, stepType).pipe(
      take(1),
      mergeMap(assigneeInfo => {
        // if (!assigneeInfo) {
        //   return of(false);
        // }
        return this.moveToNextStep(
          turnover.id,
          stepType,
          assigneeInfo?.assigneeId ?? null,
          boardId,
          assigneeInfo?.rememberIt ?? false
        ).pipe(map(() => true));
      })
    );
  }

  getAssignee(
    turnover: RestTurnoverDataModel | RestTurnoverTaskModel,
    stepType: WorkflowStepEnumType
  ): Observable<WorkflowAction> {
    if (stepType === WorkflowStepEnumType.Archive) {
      return of({} as WorkflowAction);
    }

    let turnoverData: RestTurnoverDataModel;
    let propertyId: number;
    let propertyName: string;
    if (isRestTurnoverTaskModel(turnover)) {
      turnoverData = turnover.turnoverData as RestTurnoverDataModel;
      propertyId = turnover.propertyId;
      propertyName = turnover.propertyName;
    } else {
      turnoverData = turnover;
    }
    propertyId = propertyId ?? turnoverData.property?.id;
    propertyName = propertyName ?? turnoverData.property?.name;

    const activeStep = turnoverData.activeWorkflowStepType;
    const currentAssigneeId =
      turnoverData.workflow?.find(e => e.workflowStepEnumType === stepType)?.responsiblePartyId ||
      turnoverData.workflow?.find(e => e.workflowStepEnumType === activeStep)?.responsiblePartyId;
    if (currentAssigneeId) {
      return of({ assigneeId: currentAssigneeId } as WorkflowAction);
    }
    return this.responsiblePartiesService.getResponsiblePartiesByPropertyId(propertyId).pipe(
      map(list => list?.find(step => step.workflowStepEnumType === stepType)?.responsiblePartyId),
      switchMap(defaultAssigneeId => {
        if (defaultAssigneeId) {
          return of({ assigneeId: defaultAssigneeId } as WorkflowAction);
        } else {
          if (this.isModalOpen) {
            return of(null);
          }
          this.isModalOpen = true;
          return this.modalsService
            .openNextStepAssigneeModal(turnoverData, stepType, propertyId, propertyName)
            .afterClosed()
            .pipe(
              tap(assigneeInfo => {
                if (assigneeInfo?.rememberIt) {
                  const turnDefaultAssignee: RestWorkflowStepAssignmentModel = {
                    id: 0,
                    isDefault: true,
                    workflowStepEnumType: assigneeInfo?.nextWorkflowStep,
                    propertyId: turnoverData?.property?.id,
                    responsiblePartyId: assigneeInfo?.assigneeId,
                    responsiblePartyGroupType: UserType.None,
                  };
                  this.store.dispatch(createResponsibleParties({ responsibleParties: [turnDefaultAssignee] }));
                }
                this.isModalOpen = false;
              })
            );
        }
      })
    );
  }
}
